import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ChevronRight,
  Users, 
  Lightbulb,
  Code,
  Rocket,
  Star,
  Home,
  CheckCircle,
  Target,
  Database,
  Palette,
  Calendar,
  ArrowRight,
  Building2,
  Zap,
  Lock,
  Mail,
  Globe,
  ExternalLink,
  Sparkles,
  Image as ImageIcon,
  FileText,
  Smartphone,
  CreditCard,
  LayoutDashboard,
  Upload,
  X,
  Edit3,
  Search,
  PenTool,
  Wrench,
  Package,
  Send,
  RefreshCw,
  ArrowUpRight,
  Armchair
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin, EditableText, EditableImage } from '../AdminContentEditor';

// Assets
const PHYGITAL_LOGO = 'https://customer-assets.emergentagent.com/job_brainfood-resources/artifacts/5ba8azs7_1%20copy.png';
const NAOMI_AVATAR = 'https://customer-assets.emergentagent.com/job_e02576fc-3e7a-4446-bc21-d558624baf1e/artifacts/zhpzfvij_naomi%20headshot%20zoomed%20in.jpg';

// Default tool data
const defaultTools = [
  { 
    id: 'emergent',
    name: 'Emergent', 
    description: 'AI-powered development',
    link: 'https://app.emergent.sh/?via=naomi',
    color: 'from-emerald-500 to-teal-500'
  },
  { 
    id: 'perplexity',
    name: 'Perplexity', 
    description: 'Research & analysis',
    link: 'https://perplexity.ai',
    color: 'from-cyan-500 to-blue-500'
  },
  { 
    id: 'claude',
    name: 'Claude', 
    description: 'AI assistant',
    link: 'https://claude.ai',
    color: 'from-orange-400 to-amber-500'
  },
  { 
    id: 'canva',
    name: 'Canva', 
    description: 'Design & visuals',
    link: 'https://canva.com',
    color: 'from-purple-500 to-indigo-500'
  },
  { 
    id: 'notion',
    name: 'Notion', 
    description: 'Documentation',
    link: 'https://notion.so',
    color: 'from-gray-800 to-gray-900'
  },
  { 
    id: 'beehiiv',
    name: 'Beehiiv', 
    description: 'Newsletter',
    link: 'https://beehiiv.com',
    color: 'from-amber-400 to-yellow-500'
  },
];

// Default weekly timeline data
const defaultWeeklyTimeline = [
  {
    week: 1,
    type: 'milestone',
    title: 'The Spark',
    subtitle: 'Idea & Market Research',
    description: 'The idea for Phygital was born from a personal frustration at a nail salon - having to re-explain preferences every visit. This week focused on validating the concept and understanding the market opportunity.',
    highlights: [
      'Identified core problem: preference data siloed per service provider',
      'Named the platform "Phygital" (Physical + Digital)',
      'Defined B2B2C business model: providers pay, consumers free',
      'Market research: stylists waste 15+ min per appointment on preference discovery',
      'Mapped initial verticals: Beauty, Events, Wellness, Culinary',
      'Decision: Start narrow with beauty services, prove concept, then expand'
    ],
    imageCaption: 'Initial concept sketches',
    defaultImage: 'https://customer-assets.emergentagent.com/job_datadream-lab/artifacts/ke3vg8kv_Front%20Row%20%281%29.png'
  },
  {
    week: 2,
    type: 'feature',
    title: 'Foundation',
    subtitle: 'First Code & User Interviews',
    description: 'Set up the development environment and conducted first user interviews with beauty professionals to validate assumptions.',
    highlights: [
      'Development environment set up on Emergent platform',
      'Tech stack: React + Tailwind CSS frontend, FastAPI + MongoDB backend',
      'Built "Coming Soon" landing page with email capture',
      'Implemented passwordless magic link authentication',
      'Designed brand identity: purple/pink gradient aesthetic',
      'User interviews with 2 hair stylists and 1 MUA',
      'Key finding: providers want client CONNECTION more than just data storage',
      'Created database schema: users, magic_links collections'
    ],
    imageCaption: 'Brand identity design'
  },
  {
    week: 3,
    type: 'feature',
    title: 'MVP',
    subtitle: 'Profile System & Demo Pages',
    description: 'Built the core profile system with preference categories and shareable digital cards. Created demo pages for different provider types.',
    highlights: [
      'User profile system with shareable digital cards',
      'QR code generation for profile sharing',
      'Preference categories: dietary, accessibility, interests, vibes',
      'Created demo pages: Beauty, Events, Wellness, Culinary verticals',
      'Hierarchical portal architecture for different user types',
      'Mobile-responsive design across iOS/Android',
      'Fixed preference persistence bug (MongoDB nested JSON issue)'
    ],
    imageCaption: 'MVP dashboard preview'
  },
  {
    week: 4,
    type: 'milestone',
    title: 'Pilots Secured',
    subtitle: 'Hair, MUA & Events Pivot',
    description: 'Pivoted into the Events vertical based on real-world experience at an exclusive fashion event. Secured initial pilot partners.',
    highlights: [
      'Secured 3 pilot partners: Latonya (Hair), Kiara (MUA), HTHF (Events)',
      'Pivoted to Events vertical after attending fashion event',
      'Built pilot portal system with custom onboarding flows',
      'Custom intake form builder for each pilot type',
      'Database schema: events, event_rsvps, event_vip_lists',
      'Partner communication tracking system',
      '6 portal categories: Beauty, Events, Wellness, Investors, Team, Pitches'
    ],
    imageCaption: 'Pilot partner onboarding'
  }
];

// Product Development Lifecycle phases
const lifecyclePhases = [
  { id: 'define', label: 'Define the opportunity', icon: Search, color: 'from-blue-500 to-cyan-500', phase: 'discovery' },
  { id: 'design', label: 'Design the solution', icon: PenTool, color: 'from-purple-500 to-pink-500', phase: 'discovery' },
  { id: 'build', label: 'Build the solution', icon: Wrench, color: 'from-orange-500 to-amber-500', phase: 'delivery' },
  { id: 'prepare', label: 'Prepare for launch', icon: Package, color: 'from-emerald-500 to-teal-500', phase: 'delivery' },
  { id: 'launch', label: 'Launch!', icon: Send, color: 'from-red-500 to-pink-500', phase: 'delivery' },
  { id: 'learn', label: 'Learn, maintain, improve', icon: RefreshCw, color: 'from-indigo-500 to-purple-500', phase: 'delivery' },
];

// Editable Image Placeholder Component
const EditableImagePlaceholder = ({ sectionId, imageKey, caption, className = '', defaultImage = null }) => {
  const { isAdmin, isEditMode, sectionContent, updateSectionContent } = useAdmin();
  const [uploading, setUploading] = useState(false);
  const [localImage, setLocalImage] = useState(null);
  
  const storageKey = `month1_image_${sectionId}_${imageKey}`;
  
  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      setLocalImage(saved);
    }
  }, [storageKey]);
  
  const savedImage = localImage || sectionContent?.[sectionId]?.[imageKey];
  const displayImage = savedImage || defaultImage;
  
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const imageData = event.target?.result;
      localStorage.setItem(storageKey, imageData);
      setLocalImage(imageData); // Update local state to trigger re-render
      updateSectionContent(sectionId, { [imageKey]: imageData });
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };
  
  const clearImage = () => {
    localStorage.removeItem(storageKey);
    setLocalImage(null);
    updateSectionContent(sectionId, { [imageKey]: null });
  };
  
  if (displayImage) {
    return (
      <div className={`relative rounded-2xl overflow-hidden ${className}`}>
        <img src={displayImage} alt={caption} className="w-full h-full object-cover" />
        {isAdmin && isEditMode && (
          <button
            onClick={clearImage}
            className="absolute top-2 right-2 p-2 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }
  
  return (
    <div className={`bg-gray-100 rounded-2xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center ${className}`}>
      {isAdmin && isEditMode ? (
        <label className="cursor-pointer flex flex-col items-center p-6 w-full h-full justify-center hover:bg-gray-200 transition-colors rounded-2xl">
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
          {uploading ? (
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Upload className="w-10 h-10 text-purple-500 mb-2" />
              <span className="text-sm text-purple-600 font-medium">Upload Image</span>
              <span className="text-xs text-gray-600 mt-1 text-center">{caption}</span>
            </>
          )}
        </label>
      ) : (
        <>
          <ImageIcon className="w-10 h-10 text-gray-500 mb-2" />
          <span className="text-sm text-gray-700 text-center font-medium">{caption}</span>
          <span className="text-xs text-gray-500 mt-1">Image placeholder</span>
        </>
      )}
    </div>
  );
};

// Editable Tool Card Component
const EditableToolCard = ({ tool, index }) => {
  const { isAdmin, isEditMode } = useAdmin();
  const [isEditing, setIsEditing] = useState(false);
  const [savedData, setSavedData] = useState(null);
  const [savedImage, setSavedImage] = useState(null);
  
  const storageKey = `month1_tool_${tool.id}`;
  
  // Load from localStorage on mount
  useEffect(() => {
    const data = localStorage.getItem(storageKey);
    if (data) {
      setSavedData(JSON.parse(data));
    }
    const img = localStorage.getItem(`${storageKey}_image`);
    if (img) {
      setSavedImage(img);
    }
  }, [storageKey]);
  
  const toolData = savedData || tool;
  
  const [editForm, setEditForm] = useState({
    name: toolData.name,
    description: toolData.description,
    link: toolData.link
  });
  
  // Update editForm when savedData changes
  useEffect(() => {
    if (savedData) {
      setEditForm({
        name: savedData.name || tool.name,
        description: savedData.description || tool.description,
        link: savedData.link || tool.link
      });
    }
  }, [savedData, tool]);
  
  const handleSave = () => {
    localStorage.setItem(storageKey, JSON.stringify(editForm));
    setSavedData(editForm);
    setIsEditing(false);
  };
  
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const imageData = event.target?.result;
      localStorage.setItem(`${storageKey}_image`, imageData);
      setSavedImage(imageData);
    };
    reader.readAsDataURL(file);
  };
  
  if (isEditing && isAdmin && isEditMode) {
    return (
      <div className="flex flex-col p-4 rounded-2xl bg-white border-2 border-purple-500 shadow-lg">
        <input
          type="text"
          value={editForm.name}
          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
          className="font-medium text-gray-900 text-sm mb-2 p-2 border border-gray-300 rounded-lg bg-white"
          placeholder="Tool name"
        />
        <input
          type="text"
          value={editForm.description}
          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
          className="text-sm text-gray-900 mb-2 p-2 border border-gray-300 rounded-lg bg-white"
          placeholder="Description"
        />
        <input
          type="url"
          value={editForm.link}
          onChange={(e) => setEditForm({ ...editForm, link: e.target.value })}
          className="text-sm text-gray-900 mb-2 p-2 border border-gray-300 rounded-lg bg-white"
          placeholder="https://..."
        />
        <label className="text-xs text-purple-600 cursor-pointer mb-2 p-2 border border-dashed rounded-lg text-center hover:bg-purple-50">
          <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
          Upload Logo
        </label>
        <div className="flex gap-2">
          <button onClick={handleSave} className="flex-1 py-1 bg-purple-600 text-white text-xs rounded-lg">Save</button>
          <button onClick={() => setIsEditing(false)} className="flex-1 py-1 bg-gray-200 text-gray-700 text-xs rounded-lg">Cancel</button>
        </div>
      </div>
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="relative"
    >
      <a
        href={toolData.link}
        target="_blank"
        rel="noopener noreferrer"
        className="flex flex-col items-center p-4 rounded-2xl bg-gray-50 hover:bg-gray-100 transition-all group"
      >
        <div className={`w-12 h-12 rounded-xl ${savedImage ? 'bg-white' : `bg-gradient-to-br ${tool.color}`} shadow-sm flex items-center justify-center mb-3 group-hover:shadow-md transition-shadow overflow-hidden`}>
          {savedImage ? (
            <img src={savedImage} alt={toolData.name} className="w-10 h-10 object-contain" />
          ) : (
            <span className="text-white font-bold text-lg">{toolData.name[0]}</span>
          )}
        </div>
        <span className="font-medium text-gray-900 text-sm text-center">{toolData.name}</span>
        <span className="text-xs text-gray-500 text-center">{toolData.description}</span>
      </a>
      {isAdmin && isEditMode && (
        <button
          onClick={(e) => { e.preventDefault(); setIsEditing(true); }}
          className="absolute -top-2 -right-2 p-1.5 rounded-full bg-purple-600 text-white hover:bg-purple-700 transition-colors shadow-lg"
        >
          <Edit3 className="w-3 h-3" />
        </button>
      )}
    </motion.div>
  );
};

// Editable Link Button Component
const EditableLinkButton = ({ sectionId, defaultHref, defaultText, className, icon: Icon, disabled = false }) => {
  const { isAdmin, isEditMode } = useAdmin();
  const [isEditing, setIsEditing] = useState(false);
  const [savedData, setSavedData] = useState(null);
  
  const storageKey = `month1_link_${sectionId}`;
  
  // Load from localStorage on mount
  useEffect(() => {
    const data = localStorage.getItem(storageKey);
    if (data) {
      setSavedData(JSON.parse(data));
    }
  }, [storageKey]);
  
  const linkData = savedData || { href: defaultHref, text: defaultText };
  
  const [editForm, setEditForm] = useState(linkData);
  
  // Update editForm when savedData changes
  useEffect(() => {
    if (savedData) {
      setEditForm(savedData);
    }
  }, [savedData]);
  
  const handleSave = () => {
    localStorage.setItem(storageKey, JSON.stringify(editForm));
    setSavedData(editForm);
    setIsEditing(false);
  };
  
  if (isEditing && isAdmin && isEditMode) {
    return (
      <div className="inline-flex flex-col gap-2 p-4 bg-white border-2 border-purple-500 rounded-xl shadow-lg">
        <input
          type="url"
          value={editForm.href}
          onChange={(e) => setEditForm({ ...editForm, href: e.target.value })}
          className="p-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white"
          placeholder="https://..."
        />
        <input
          type="text"
          value={editForm.text}
          onChange={(e) => setEditForm({ ...editForm, text: e.target.value })}
          className="p-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white"
          placeholder="Button text"
        />
        <div className="flex gap-2">
          <button onClick={handleSave} className="flex-1 py-2 bg-purple-600 text-white text-sm rounded-lg">Save</button>
          <button onClick={() => setIsEditing(false)} className="flex-1 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg">Cancel</button>
        </div>
      </div>
    );
  }
  
  if (disabled && !isAdmin) {
    return (
      <button disabled className={className}>
        {Icon && <Icon className="w-4 h-4" />}
        {linkData.text}
      </button>
    );
  }
  
  // If admin, make the disabled button editable
  if (disabled && isAdmin && isEditMode) {
    return (
      <div className="relative inline-block w-full">
        <button className={className}>
          {Icon && <Icon className="w-4 h-4" />}
          {linkData.text}
        </button>
        <button
          onClick={() => setIsEditing(true)}
          className="absolute -top-2 -right-2 p-1.5 rounded-full bg-purple-600 text-white hover:bg-purple-700 transition-colors shadow-lg z-10"
        >
          <Edit3 className="w-3 h-3" />
        </button>
      </div>
    );
  }
  
  if (disabled) {
    return (
      <button disabled className={className}>
        {Icon && <Icon className="w-4 h-4" />}
        {linkData.text}
      </button>
    );
  }
  
  return (
    <div className="relative inline-block">
      <a
        href={linkData.href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        data-testid={`${sectionId}-link`}
      >
        {Icon && <Icon className="w-5 h-5" />}
        {linkData.text}
        <ArrowRight className="w-5 h-5" />
      </a>
      {isAdmin && isEditMode && (
        <button
          onClick={() => setIsEditing(true)}
          className="absolute -top-2 -right-2 p-1.5 rounded-full bg-purple-600 text-white hover:bg-purple-700 transition-colors shadow-lg"
        >
          <Edit3 className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

// Editable Text Inline Component
const EditableTextInline = ({ sectionId, textKey, defaultText, className, tag: Tag = 'span' }) => {
  const { isAdmin, isEditMode } = useAdmin();
  const [isEditing, setIsEditing] = useState(false);
  const [savedText, setSavedText] = useState(null);
  
  const storageKey = `month1_text_${sectionId}_${textKey}`;
  
  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      setSavedText(saved);
    }
  }, [storageKey]);
  
  const displayText = savedText || defaultText;
  const [editText, setEditText] = useState(displayText);
  
  // Update editText when savedText changes
  useEffect(() => {
    setEditText(savedText || defaultText);
  }, [savedText, defaultText]);
  
  const handleSave = () => {
    localStorage.setItem(storageKey, editText);
    setSavedText(editText);
    setIsEditing(false);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setEditText(displayText);
    }
  };
  
  if (isEditing && isAdmin && isEditMode) {
    return (
      <div className="inline-flex items-center gap-2">
        {Tag === 'p' || defaultText.length > 100 ? (
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            className="border-2 border-purple-500 rounded-lg p-2 min-w-[200px] text-gray-900 bg-white text-sm"
            autoFocus
            rows={3}
          />
        ) : (
          <input
            type="text"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            className="border-2 border-purple-500 rounded-lg px-2 py-1 min-w-[100px] text-gray-900 bg-white text-sm"
            autoFocus
          />
        )}
      </div>
    );
  }
  
  return (
    <Tag 
      className={`${className} ${isAdmin && isEditMode ? 'cursor-pointer hover:bg-purple-100 hover:outline hover:outline-2 hover:outline-purple-300 rounded transition-all' : ''}`}
      onClick={() => isAdmin && isEditMode && setIsEditing(true)}
    >
      {displayText}
    </Tag>
  );
};

// Access Gate Component
const AccessGate = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-white">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="w-full max-w-md text-center"
      >
        <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-6">
          <Lock className="w-10 h-10 text-gray-400" />
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Required</h2>
        <p className="text-gray-600 mb-8">
          You need to be approved through the Data Dreaming invite to access Month 1 content.
        </p>
        
        <button
          onClick={() => navigate('/lab/frontrow/phygital')}
          className="w-full py-4 rounded-xl bg-black text-white font-medium hover:bg-gray-800 transition-colors mb-4"
        >
          Go to Front Row
        </button>
        
        <Link to="/" className="inline-flex items-center gap-2 text-gray-500 text-sm hover:text-gray-700">
          <Home className="w-4 h-4" />
          Back to Data Dreaming
        </Link>
      </motion.div>
    </div>
  );
};

// Product Development Lifecycle Component
const ProductLifecycle = () => {
  const { isAdmin, isEditMode } = useAdmin();
  
  return (
    <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <EditableTextInline
            sectionId="lifecycle"
            textKey="title"
            defaultText="Product Development Lifecycle"
            className="text-2xl font-bold text-gray-900 block mb-2"
            tag="h2"
          />
          <EditableTextInline
            sectionId="lifecycle"
            textKey="subtitle"
            defaultText="The journey from idea to continuous improvement"
            className="text-gray-600"
            tag="p"
          />
        </motion.div>

        {/* Lifecycle Visual */}
        <div className="relative">
          {/* Phase Labels */}
          <div className="flex justify-center gap-8 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-sm font-medium text-gray-700">Discovery</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500" />
              <span className="text-sm font-medium text-gray-700">Delivery</span>
            </div>
          </div>

          {/* Main Lifecycle Flow */}
          <div className="relative flex flex-wrap justify-center items-center gap-4 md:gap-2">
            {lifecyclePhases.map((phase, index) => {
              const Icon = phase.icon;
              const isDiscovery = phase.phase === 'discovery';
              
              return (
                <motion.div
                  key={phase.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative flex items-center"
                >
                  {/* Arrow connector (except for first item) */}
                  {index > 0 && (
                    <div className="hidden md:flex items-center mr-2">
                      <ArrowRight className="w-5 h-5 text-gray-300" />
                    </div>
                  )}
                  
                  {/* Phase Card */}
                  <div className={`flex flex-col items-center p-4 rounded-2xl border-2 transition-all hover:shadow-lg ${
                    isDiscovery 
                      ? 'bg-blue-50 border-blue-200 hover:border-blue-400' 
                      : 'bg-orange-50 border-orange-200 hover:border-orange-400'
                  }`}>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${phase.color} flex items-center justify-center mb-3 shadow-md`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <EditableTextInline
                      sectionId={`lifecycle-${phase.id}`}
                      textKey="label"
                      defaultText={phase.label}
                      className="text-sm font-medium text-gray-800 text-center max-w-[100px]"
                      tag="span"
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Feedback Loop Arrow */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.8 }}
            className="flex justify-center mt-8"
          >
            <div className="flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-indigo-100 to-purple-100 border border-indigo-200">
              <RefreshCw className="w-5 h-5 text-indigo-600" />
              <span className="text-sm font-medium text-indigo-700">Continuous feedback loop</span>
              <ArrowUpRight className="w-4 h-4 text-indigo-600" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Timeline Entry Component
const TimelineEntry = ({ entry, index }) => {
  const isMilestone = entry.type === 'milestone';
  const sectionId = `week-${entry.week}`;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.1 }}
      className="relative"
    >
      {/* Timeline connector */}
      <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />
      
      {/* Timeline dot */}
      <div className={`absolute left-4 top-8 w-5 h-5 rounded-full border-2 border-white shadow-sm flex items-center justify-center z-10 ${
        isMilestone ? 'bg-amber-400' : 'bg-emerald-400'
      }`}>
        {isMilestone ? (
          <Star className="w-3 h-3 text-white" />
        ) : (
          <CheckCircle className="w-3 h-3 text-white" />
        )}
      </div>
      
      {/* Content Card */}
      <div className="ml-14 pb-12">
        {/* Week badge */}
        <div className="flex items-center gap-3 mb-3">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            isMilestone 
              ? 'bg-amber-100 text-amber-700' 
              : 'bg-emerald-100 text-emerald-700'
          }`}>
            {isMilestone ? '★ milestone' : 'feature'}
          </span>
          <span className="text-sm text-gray-500">Week {entry.week}</span>
        </div>
        
        {/* Title & Description */}
        <EditableTextInline
          sectionId={sectionId}
          textKey="title"
          defaultText={entry.title}
          className="text-xl font-bold text-gray-900 block mb-1"
          tag="h3"
        />
        <EditableTextInline
          sectionId={sectionId}
          textKey="subtitle"
          defaultText={entry.subtitle}
          className="text-gray-600 font-medium block mb-3"
          tag="p"
        />
        <EditableTextInline
          sectionId={sectionId}
          textKey="description"
          defaultText={entry.description}
          className="text-gray-500 text-sm block mb-4 leading-relaxed max-w-prose"
          tag="p"
        />
        
        {/* Highlights */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" />
            Highlights
          </h4>
          <ul className="space-y-2">
            {entry.highlights.map((highlight, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
                <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <EditableTextInline
                  sectionId={sectionId}
                  textKey={`highlight-${i}`}
                  defaultText={highlight}
                  className="flex-1"
                  tag="span"
                />
              </li>
            ))}
          </ul>
        </div>
        
        {/* Image Placeholder */}
        <EditableImagePlaceholder
          sectionId={sectionId}
          imageKey="main-image"
          caption={entry.imageCaption}
          className="w-full aspect-video"
          defaultImage={entry.defaultImage}
        />
      </div>
    </motion.div>
  );
};

// Month 2 Sneak Peek Component - Stacked Layout
const Month2SneakPeek = () => (
  <motion.section
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="py-20 px-4 bg-gradient-to-b from-white to-gray-50"
  >
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-12">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 text-purple-600 text-sm font-medium mb-4">
          <Sparkles className="w-4 h-4" />
          Coming Soon
        </span>
        <EditableTextInline
          sectionId="month2"
          textKey="title"
          defaultText="Month 2 Sneak Peek"
          className="text-3xl md:text-4xl font-bold text-gray-900 block mb-2"
          tag="h2"
        />
        <EditableTextInline
          sectionId="month2"
          textKey="subtitle"
          defaultText="What's coming in February"
          className="text-gray-600"
          tag="p"
        />
      </div>
      
      {/* Stacked Cards - Centered */}
      <div className="flex flex-col gap-8 items-center">
        {/* HTHF Event Card */}
        <div className="bg-white rounded-3xl border border-gray-200 p-8 shadow-sm w-full max-w-lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Calendar className="w-7 h-7 text-white" />
            </div>
            <div>
              <EditableTextInline
                sectionId="month2-hthf"
                textKey="title"
                defaultText="HTHF Event"
                className="font-bold text-gray-900 text-lg block"
                tag="h3"
              />
              <EditableTextInline
                sectionId="month2-hthf"
                textKey="subtitle"
                defaultText="High Tech High Fashion"
                className="text-sm text-gray-500"
                tag="span"
              />
            </div>
          </div>
          
          <EditableTextInline
            sectionId="month2-hthf"
            textKey="description"
            defaultText="Live event during LA Fashion Week with Apple Wallet integration for VIP access and check-ins."
            className="text-gray-600 text-sm mb-6 block text-center"
            tag="p"
          />
          
          {/* Before/After Apple Wallet Placeholders - SQUARE */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <EditableImagePlaceholder
              sectionId="month2-hthf"
              imageKey="apple-wallet-before"
              caption="Before - Apple Wallet"
              className="aspect-square"
            />
            <EditableImagePlaceholder
              sectionId="month2-hthf"
              imageKey="apple-wallet-after"
              caption="After - Apple Wallet"
              className="aspect-square"
              defaultImage="https://customer-assets.emergentagent.com/job_datadream-lab/artifacts/mdc2fdag_Front%20Row.png"
            />
          </div>
          
          {/* HTHF Link Button - Editable */}
          <EditableLinkButton
            sectionId="hthf-event"
            defaultHref="#"
            defaultText="HTHF Event Link (Coming Soon)"
            icon={ExternalLink}
            disabled={true}
            className="w-full py-3 rounded-xl bg-gray-100 text-gray-400 font-medium flex items-center justify-center gap-2 cursor-not-allowed"
          />
        </div>
        
        {/* Admin Dashboard Card */}
        <div className="bg-white rounded-3xl border border-gray-200 p-8 shadow-sm w-full max-w-lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center">
              <LayoutDashboard className="w-7 h-7 text-white" />
            </div>
            <div>
              <EditableTextInline
                sectionId="month2-admin"
                textKey="title"
                defaultText="Admin Dashboard"
                className="font-bold text-gray-900 text-lg block"
                tag="h3"
              />
              <EditableTextInline
                sectionId="month2-admin"
                textKey="subtitle"
                defaultText="Internal tools & analytics"
                className="text-sm text-gray-500"
                tag="span"
              />
            </div>
          </div>
          
          <EditableTextInline
            sectionId="month2-admin"
            textKey="description"
            defaultText="Full admin panel for managing events, users, analytics, and content across all Phygital properties."
            className="text-gray-600 text-sm mb-6 block text-center"
            tag="p"
          />
          
          {/* Dashboard Preview Placeholder */}
          <EditableImagePlaceholder
            sectionId="month2-admin"
            imageKey="dashboard-preview"
            caption="Dashboard Preview"
            className="w-full aspect-video mb-6"
            defaultImage="https://customer-assets.emergentagent.com/job_datadream-lab/artifacts/hg27iskc_Front%20Row%20%282%29.png"
          />
          
          {/* Admin Dashboard Link - Editable */}
          <EditableLinkButton
            sectionId="admin-dashboard"
            defaultHref="#"
            defaultText="Admin Dashboard (Coming Soon)"
            icon={ExternalLink}
            disabled={true}
            className="w-full py-3 rounded-xl bg-gray-100 text-gray-400 font-medium flex items-center justify-center gap-2 cursor-not-allowed"
          />
        </div>
      </div>
    </div>
  </motion.section>
);

// Main Component
const Month1Workshop = () => {
  const [hasAccess, setHasAccess] = useState(false);
  const { isAdmin, isEditMode } = useAdmin();

  useEffect(() => {
    const userEmail = localStorage.getItem('soiree_email') || localStorage.getItem('frontrow_email');
    if (userEmail && userEmail.toLowerCase() === 'me@iamnaomithomas.com') {
      setHasAccess(true);
      return;
    }
    
    const approved = localStorage.getItem('frontrow_approved');
    if (approved) {
      setHasAccess(true);
    }
  }, []);

  if (!hasAccess) return <AccessGate />;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/lab/frontrow/phygital" className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors">
              <Home className="w-4 h-4 text-gray-700" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-black flex items-center justify-center">
                <Armchair className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-gray-900">Month 1: The Spark</span>
            </div>
          </div>
          <span className="text-sm text-gray-500">January 2026</span>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4 text-center border-b border-gray-100">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-600 text-sm font-medium mb-6">
              <Calendar className="w-4 h-4" />
              January 2026
            </div>
            <EditableTextInline
              sectionId="hero"
              textKey="title"
              defaultText="From Idea to MVP"
              className="text-4xl md:text-5xl font-bold text-gray-900 block mb-4"
              tag="h1"
            />
            <EditableTextInline
              sectionId="hero"
              textKey="subtitle"
              defaultText="The first month of building Phygital"
              className="text-xl text-gray-600 block mb-2"
              tag="p"
            />
            <EditableTextInline
              sectionId="hero"
              textKey="description"
              defaultText="Turned a nail salon visit into a tech startup. Built the MVP, secured 3 pilots, and established the foundation."
              className="text-gray-500 block max-w-xl mx-auto text-balance"
              tag="p"
            />
          </motion.div>
        </div>
      </section>

      {/* Meet Your Guide Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Meet Your Guide</h2>
            <p className="text-gray-600">The person behind Phygital</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl border border-gray-200 p-8 shadow-sm mb-12"
          >
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="relative">
                <EditableImagePlaceholder
                  sectionId="guide"
                  imageKey="avatar"
                  caption="Profile photo"
                  className="w-32 h-32 rounded-2xl"
                />
                {!localStorage.getItem('month1_image_guide_avatar') && (
                  <img 
                    src={NAOMI_AVATAR} 
                    alt="Naomi Thomas" 
                    className="w-32 h-32 rounded-2xl object-cover shadow-lg absolute inset-0"
                  />
                )}
              </div>
              <div className="text-center md:text-left flex-1">
                <EditableTextInline
                  sectionId="guide"
                  textKey="name"
                  defaultText="Naomi Thomas"
                  className="text-2xl font-bold text-gray-900 block mb-1"
                  tag="h3"
                />
                <EditableTextInline
                  sectionId="guide"
                  textKey="title"
                  defaultText="Founder, Phygital"
                  className="text-purple-600 font-medium block mb-4"
                  tag="p"
                />
                <EditableTextInline
                  sectionId="guide"
                  textKey="bio"
                  defaultText="AI Strategist and Data Dreamer. Building Phygital to solve the preference communication problem in service industries. Background in tech consulting, passion for bridging physical and digital experiences."
                  className="text-gray-600 leading-relaxed block mb-4 max-w-prose"
                  tag="p"
                />
                <div className="flex flex-wrap justify-center md:justify-start gap-2">
                  <span className="px-3 py-1 rounded-full bg-purple-100 text-purple-600 text-sm">AI Strategy</span>
                  <span className="px-3 py-1 rounded-full bg-pink-100 text-pink-600 text-sm">Product</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Tools Used */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-6">Tools Used This Month</h3>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
              {defaultTools.map((tool, index) => (
                <EditableToolCard key={tool.id} tool={tool} index={index} />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product Development Lifecycle - Before Weekly Timeline */}
      <ProductLifecycle />

      {/* Weekly Timeline */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Week by Week</h2>
            <p className="text-gray-600">A detailed timeline of Month 1 progress</p>
          </motion.div>

          {/* Timeline */}
          <div className="relative">
            {defaultWeeklyTimeline.map((entry, index) => (
              <TimelineEntry key={entry.week} entry={entry} index={index} />
            ))}
            
            {/* End of timeline marker */}
            <div className="absolute left-4 bottom-0 w-5 h-5 rounded-full bg-gray-300 border-2 border-white shadow-sm flex items-center justify-center z-10">
              <CheckCircle className="w-3 h-3 text-white" />
            </div>
          </div>
        </div>
      </section>

      {/* Pitch Deck Link */}
      <section className="py-16 px-4 bg-gradient-to-r from-purple-600 to-pink-600">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mx-auto mb-6">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">View the Pitch Deck</h2>
            <p className="text-white/80 mb-8 max-w-md mx-auto text-balance">
              See how we presented Phygital to investors and partners at the end of Month 1.
            </p>
            <EditableLinkButton
              sectionId="pitch-deck"
              defaultHref="https://phygital.systems/pitchdeck"
              defaultText="Open Pitch Deck"
              icon={FileText}
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-purple-600 font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            />
          </motion.div>
        </div>
      </section>

      {/* Month 2 Sneak Peek - Stacked Layout */}
      <Month2SneakPeek />

      {/* Newsletter QR Code Section - After Month 2 */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Stay in the Loop
              </h2>
              <p className="text-gray-600 text-lg max-w-md mx-auto text-balance">
                Get notified when new monthly updates drop. Scan the QR code or click to subscribe.
              </p>
            </div>

            {/* QR Code Card - Centered */}
            <div className="flex justify-center">
              <div className="p-8 rounded-3xl bg-white border border-gray-200 shadow-lg">
                <a 
                  href="https://subscribe-forms.beehiiv.com/1c865009-d66c-430c-9cd6-1ca506583168" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block"
                  data-testid="newsletter-qr-link"
                >
                  <img 
                    src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://subscribe-forms.beehiiv.com/1c865009-d66c-430c-9cd6-1ca506583168&bgcolor=FFFFFF&color=000000&margin=0"
                    alt="Subscribe to Data Dreaming Newsletter"
                    className="w-48 h-48 mx-auto mb-4"
                  />
                </a>
                <p className="text-gray-900 font-semibold mb-1">Data Dreaming Newsletter</p>
                <p className="text-gray-500 text-sm">Monthly startup insights & updates</p>
              </div>
            </div>

            {/* Subscribe Button */}
            <div className="flex justify-center">
              <EditableLinkButton
                sectionId="newsletter"
                defaultHref="https://subscribe-forms.beehiiv.com/1c865009-d66c-430c-9cd6-1ca506583168"
                defaultText="Subscribe to Newsletter"
                icon={Mail}
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-black text-white font-medium hover:bg-gray-800 transition-colors"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-sm">Powered by</span>
            <img src={PHYGITAL_LOGO} alt="Phygital" className="h-8 object-contain" />
          </div>
          <Link to="/lab/frontrow/phygital" className="text-gray-500 text-sm hover:text-gray-600">
            Back to Front Row
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default Month1Workshop;
